import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { ddmmyyyy, inr, num, todayIST } from "../lib/format";
import { StickyBar, Field, PageHead } from "../components/FormBits";
import { AddLine } from "../components/jobs/ShirtStock";
import { NameList, useMaster } from "../components/setup/PartiesTab";

const uid = () => String(Math.random()).slice(2);

export function ChallanForm() {
  const navigate = useNavigate();
  const pre = useLocation().state?.prefill;
  const [stock, setStock] = useState(null);
  const [locs, setLocs] = useState([]);
  const [f, setF] = useState({ date: todayIST(), from_location: pre?.from_location || "", to_location: pre?.to_location || "", transporter: "", vehicle: "", remarks: pre ? "Refill to minimum stock" : "" });
  const [transporters] = useMaster("transporters");
  const [lines, setLines] = useState(pre ? pre.items.map((i) => ({ uid: uid(), key: i.key, qty: String(i.qty) })) : [{ uid: uid(), key: "", qty: "" }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/fg-stock"), api.get("/locations")])
      .then(([s, l]) => { setStock(s.data.locations); setLocs(l.data); })
      .catch((e) => toast.error(errMsg(e)));
  }, []);
  const avail = useMemo(() => stock?.find((l) => l.location === f.from_location)?.items.filter((i) => i.qty > 0) || [], [stock, f.from_location]);
  if (!stock) return <p className="text-muted-foreground">Loading…</p>;
  const total = lines.reduce((a, l) => a + (Number(l.qty) || 0), 0);
  const setLine = (u, p) => setLines((ls) => ls.map((l) => (l.uid === u ? { ...l, ...p } : l)));

  const save = async () => {
    if (!f.from_location || !f.to_location) return toast.error("Select From and To locations");
    const items = lines.filter((l) => l.key && Number(l.qty) > 0).map((l) => ({ key: l.key, qty: Number(l.qty) }));
    if (!items.length) return toast.error("Add at least one item with quantity");
    setSaving(true);
    try {
      const { data } = await api.post("/challans", { ...f, items });
      toast.success(`${data.dc_no} created`);
      navigate("/jobs", { state: { view: "shirts" } });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="challan-form">
      <PageHead kicker="Stock transfer" title="New delivery challan" sub="Shirts leave the From location now and reach the To location when received." />
      <section className="panel rise grid grid-cols-1 gap-4 p-4 min-[400px]:grid-cols-2">
        <Field label="Date"><input type="date" className="field" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="challan-date-input" /></Field>
        <Field label="Transporter"><input className="field" list="challan-transporters" value={f.transporter} onChange={(e) => setF({ ...f, transporter: e.target.value })} placeholder="Pick or type" data-testid="challan-transporter-input" /><NameList id="challan-transporters" list={transporters} /></Field>
        <Field label="Vehicle no"><input className="field" value={f.vehicle} onChange={(e) => setF({ ...f, vehicle: e.target.value })} data-testid="challan-vehicle-input" /></Field>
        <Field label="From *">
          <select className="field" value={f.from_location} onChange={(e) => { const v = e.target.value; setF({ ...f, from_location: v, to_location: f.to_location === v ? "" : f.to_location }); setLines([{ uid: uid(), key: "", qty: "" }]); }} data-testid="challan-from-select">
            <option value="">Select…</option>
            {stock.filter((l) => l.total > 0).map((l) => <option key={l.location} value={l.location}>{l.location} ({num(l.total)} pcs)</option>)}
          </select>
        </Field>
        <Field label="To *">
          <select className="field" value={f.to_location} onChange={(e) => setF({ ...f, to_location: e.target.value })} data-testid="challan-to-select">
            <option value="">Select…</option>
            {locs.filter((l) => l.name !== f.from_location).map((l) => <option key={l.id} value={l.name}>{l.name} ({l.kind})</option>)}
          </select>
        </Field>
      </section>
      {f.from_location && lines.map((l, i) => {
        const it = avail.find((a) => a.key === l.key);
        return (
          <div key={l.uid} className="panel rise space-y-3 p-4" data-testid={`challan-line-${i}`}>
            <div className="flex items-center justify-between">
              <span className="font-heading font-bold">Item {i + 1}</span>
              <button type="button" className="icon-btn hover:!text-destructive" onClick={() => setLines(lines.filter((x) => x.uid !== l.uid))} aria-label="Remove item" data-testid={`challan-line-${i}-remove-btn`}><Trash2 size={18} /></button>
            </div>
            <select className="field" value={l.key} onChange={(e) => setLine(l.uid, { key: e.target.value })} data-testid={`challan-line-${i}-item-select`}>
              <option value="">Select item…</option>
              {avail.filter((a) => a.key === l.key || !lines.some((x) => x.uid !== l.uid && x.key === a.key)).map((a) => <option key={a.key} value={a.key}>{a.kind === "Pants" ? "[Pant] " : ""}{a.description}{a.style ? ` · ${a.style}` : ""} ({num(a.qty)} in stock)</option>)}
            </select>
            <Field label={`Quantity${it ? ` (max ${num(it.qty)})` : ""}`}>
              <input className="field mono" type="number" inputMode="numeric" min="0" step="any" value={l.qty} onChange={(e) => setLine(l.uid, { qty: e.target.value })} data-testid={`challan-line-${i}-qty-input`} />
            </Field>
            {it && Number(l.qty) > it.qty && <p className="text-xs font-semibold text-destructive">More than in stock at {f.from_location}</p>}
          </div>
        );
      })}
      {f.from_location && <AddLine onClick={() => setLines([...lines, { uid: uid(), key: "", qty: "" }])} />}
      <section className="panel p-4"><Field label="Remarks"><textarea className="field" rows={2} value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="challan-remarks-input" /></Field></section>
      <StickyBar onSave={save} saving={saving} label="Save challan" testid="challan">
        <p className="mono-label text-muted-foreground">Shirts sent</p>
        <p className="mono text-xl font-bold" data-testid="challan-total">{num(total)}</p>
      </StickyBar>
    </div>
  );
}

export function SaleForm() {
  const navigate = useNavigate();
  const [stock, setStock] = useState(null);
  const [f, setF] = useState({ date: todayIST(), location: "", remarks: "", bill_no: "", customer_name: "", customer_mobile: "", customer_gstin: "" });
  const [customers] = useMaster("customers");
  const [search, setSearch] = useState("");
  const [lines, setLines] = useState([{ uid: uid(), key: "", qty: "", rate: "", gst: "5" }]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/fg-stock").then((s) => setStock(s.data.locations)).catch((e) => toast.error(errMsg(e)));
  }, []);
  const all = useMemo(() => stock?.find((l) => l.location === f.location)?.items.filter((i) => i.qty > 0) || [], [stock, f.location]);
  const chosen = new Set(lines.map((l) => l.key));
  const s = search.trim().toLowerCase();
  const avail = all.filter((i) => !s || chosen.has(i.key) || `${i.description} ${i.code} ${i.style}`.toLowerCase().includes(s));
  if (!stock) return <p className="text-muted-foreground">Loading…</p>;
  const total = lines.reduce((a, l) => a + (Number(l.qty) || 0), 0);
  const value = lines.reduce((a, l) => a + (Number(l.qty) || 0) * (Number(l.rate) || 0), 0);
  const tax = lines.reduce((a, l) => a + ((Number(l.qty) || 0) * (Number(l.rate) || 0) * (Number(l.gst) || 0)) / 100, 0);
  const pickCustomer = (name) => {
    const c = customers.find((x) => x.name.toLowerCase() === name.trim().toLowerCase());
    setF((x) => {
      if (c) return { ...x, customer_name: name, customer_mobile: c.phone, customer_gstin: c.gstin, auto: true };
      return { ...x, customer_name: name, ...(x.auto ? { customer_mobile: "", customer_gstin: "", auto: false } : {}) };
    });
  };
  const setLine = (u, p) => setLines((ls) => ls.map((l) => (l.uid === u ? { ...l, ...p } : l)));
  const save = async () => {
    if (!f.location) return toast.error("Select the location");
    const items = lines.filter((l) => l.key && Number(l.qty) > 0).map((l) => ({ key: l.key, qty: Number(l.qty), rate: Number(l.rate) || 0, gst: Number(l.gst) || 0 }));
    if (!items.length) return toast.error("Add at least one item with quantity");
    setSaving(true);
    try {
      const { data } = await api.post("/sales", { ...f, items });
      toast.success(`${data.sale_no} saved`);
      navigate("/jobs", { state: { view: "daily" } });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-4" data-testid="sale-form">
      <PageHead kicker="Stock" title="Record sale" sub="Shirts and pants sold are taken out of that location's stock." />
      <section className="panel grid grid-cols-1 gap-4 p-4 min-[400px]:grid-cols-2">
        <Field label="Date"><input type="date" className="field" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="sale-date-input" /></Field>
        <Field label="Bill / invoice no"><input className="field" value={f.bill_no} onChange={(e) => setF({ ...f, bill_no: e.target.value })} data-testid="sale-bill-no-input" /></Field>
        <Field label="Location *">
          <select className="field" value={f.location} onChange={(e) => { setF({ ...f, location: e.target.value }); setLines([{ uid: uid(), key: "", qty: "", rate: "", gst: "5" }]); }} data-testid="sale-location-select">
            <option value="">Select…</option>
            {stock.filter((l) => l.total > 0).map((l) => <option key={l.location} value={l.location}>{l.location} ({num(l.total)} pcs)</option>)}
          </select>
        </Field>
      </section>
      {f.location && <section className="panel p-4"><Field label="Search item (name, code, colour)"><input className="field" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="e.g. P0001 or Navy" data-testid="sale-item-search-input" /></Field></section>}
      {f.location && lines.map((l, i) => {
        const it = all.find((a) => a.key === l.key);
        return (
          <div key={l.uid} className="panel space-y-3 p-4" data-testid={`sale-line-${i}`}>
            <div className="flex items-center justify-between">
              <span className="font-heading font-bold">Item {i + 1}</span>
              <button type="button" className="icon-btn hover:!text-destructive" onClick={() => setLines(lines.filter((x) => x.uid !== l.uid))} aria-label="Remove item" data-testid={`sale-line-${i}-remove-btn`}><Trash2 size={18} /></button>
            </div>
            <select className="field" value={l.key} onChange={(e) => setLine(l.uid, { key: e.target.value })} data-testid={`sale-line-${i}-item-select`}>
              <option value="">Select item…</option>
              {avail.filter((a) => a.key === l.key || !lines.some((x) => x.uid !== l.uid && x.key === a.key)).map((a) => <option key={a.key} value={a.key}>{a.kind === "Pants" ? "[Pant] " : ""}{a.description}{a.style ? ` · ${a.style}` : ""} ({num(a.qty)} in stock)</option>)}
            </select>
            <div className="grid grid-cols-3 gap-3">
              <Field label={`Qty${it ? ` (max ${num(it.qty)})` : ""}`}>
                <input className="field mono" type="number" inputMode="numeric" min="0" step="any" value={l.qty} onChange={(e) => setLine(l.uid, { qty: e.target.value })} data-testid={`sale-line-${i}-qty-input`} />
              </Field>
              <Field label="Rate ₹ (before GST)">
                <input className="field mono" type="number" inputMode="decimal" min="0" step="any" value={l.rate} onChange={(e) => setLine(l.uid, { rate: e.target.value })} data-testid={`sale-line-${i}-rate-input`} />
              </Field>
              <Field label="GST %">
                <select className="field mono" value={l.gst} onChange={(e) => setLine(l.uid, { gst: e.target.value })} data-testid={`sale-line-${i}-gst-select`}>
                  {["0", "5", "12", "18"].map((g) => <option key={g} value={g}>{g}%</option>)}
                </select>
              </Field>
            </div>
            {Number(l.qty) > 0 && Number(l.rate) > 0 && <p className="mono text-right text-sm font-semibold" data-testid={`sale-line-${i}-value`}>{inr(Number(l.qty) * Number(l.rate) * (1 + (Number(l.gst) || 0) / 100))}</p>}
          </div>
        );
      })}
      {f.location && <AddLine onClick={() => setLines([...lines, { uid: uid(), key: "", qty: "", rate: "", gst: "5" }])} />}
      <section className="panel grid grid-cols-1 gap-4 p-4 min-[400px]:grid-cols-2" data-testid="sale-customer">
        <Field label="Customer name"><input className="field" list="sale-customers" value={f.customer_name} onChange={(e) => pickCustomer(e.target.value)} placeholder="Pick or type a new one" data-testid="sale-customer-name-input" /><NameList id="sale-customers" list={customers} /></Field>
        <Field label="Customer mobile"><input className="field" inputMode="tel" value={f.customer_mobile} onChange={(e) => setF({ ...f, customer_mobile: e.target.value })} data-testid="sale-customer-mobile-input" /></Field>
        <Field label="Customer GSTIN"><input className="field" maxLength={15} value={f.customer_gstin} onChange={(e) => setF({ ...f, customer_gstin: e.target.value })} data-testid="sale-customer-gstin-input" /></Field>
      </section>
      <section className="panel p-4"><Field label="Remarks"><textarea className="field" rows={2} value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="sale-remarks-input" /></Field></section>
      <StickyBar onSave={save} saving={saving} label="Save sale" testid="sale">
        <p className="mono-label text-muted-foreground" data-testid="sale-tax">{num(total)} pcs · GST {inr(tax)}</p>
        <p className="mono text-xl font-bold" data-testid="sale-total">{inr(value + tax)}</p>
      </StickyBar>
    </div>
  );
}

export function ChallanReceive() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [rec, setRec] = useState({});
  const [f, setF] = useState({ date: todayIST(), remarks: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/challans/${id}`).then((r) => { setC(r.data); setRec(Object.fromEntries(r.data.items.map((i) => [i.key, i.qty]))); })
      .catch((e) => toast.error(errMsg(e)));
  }, [id]);
  if (!c) return <p className="text-muted-foreground">Loading…</p>;
  const got = c.items.reduce((a, i) => a + (Number(rec[i.key]) || 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/challans/${id}/receive`, { ...f, items: c.items.map((i) => ({ key: i.key, received: Number(rec[i.key]) || 0 })) });
      toast.success(`${c.dc_no} received at ${c.to_location}`);
      navigate("/jobs", { state: { view: "shirts" } });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="challan-receive-form">
      <PageHead kicker={`${c.dc_no} · ${ddmmyyyy(c.date)}`} title={`Receive at ${c.to_location}`} sub={`From ${c.from_location}${c.transporter ? ` · ${c.transporter}` : ""}${c.vehicle ? ` · ${c.vehicle}` : ""}`} />
      <section className="panel p-4"><Field label="Received date"><input type="date" className="field" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="challan-receive-date-input" /></Field></section>
      {c.items.map((i, n) => {
        const short = i.qty - (Number(rec[i.key]) || 0);
        return (
          <div key={i.key} className="panel space-y-2 p-4" data-testid={`challan-receive-line-${n}`}>
            <p className="font-semibold">{i.description} <span className="text-xs text-muted-foreground">{i.style}</span></p>
            <p className="text-xs text-muted-foreground">Sent {num(i.qty)}</p>
            <input className="field mono" type="number" inputMode="numeric" min="0" max={i.qty} step="any" value={rec[i.key]} onChange={(e) => setRec({ ...rec, [i.key]: e.target.value })} data-testid={`challan-receive-line-${n}-qty-input`} />
            {short > 0 && <p className="mono text-xs font-bold text-destructive" data-testid={`challan-receive-line-${n}-short`}>Short {num(short)}</p>}
          </div>
        );
      })}
      <section className="panel p-4"><Field label="Remarks"><textarea className="field" rows={2} value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="challan-receive-remarks-input" /></Field></section>
      <StickyBar onSave={save} saving={saving} label="Confirm receipt" testid="challan-receive">
        <p className="mono-label text-muted-foreground">Received</p>
        <p className="mono text-xl font-bold">{num(got)} / {num(c.qty_total)}</p>
      </StickyBar>
    </div>
  );
}
